import React, { Component, type ErrorInfo, type ReactNode } from "react";

type Props = {
  children: ReactNode;
  fallback?: ReactNode;
  resetKey?: unknown;
};

type State = {
  failed: boolean;
};

export function PageErrorFallback() {
  return (
    <div className="main-content">
      <div className="page-content">
        <div className="alert alert-danger" role="alert">
          <h4>Something went wrong on this page</h4>
          <p className="margin-bottom-0">
            The page could not be displayed. Reload to try again, or use the menu to go somewhere else. If it keeps
            happening, let us know what you were doing at the time.
          </p>
        </div>
      </div>
    </div>
  );
}

// Without a boundary, React unmounts the entire root when any render throws, so
// a single bad component blanks the whole application -- chrome, navigation and
// all. Containing the failure keeps the surrounding shell usable and leaves the
// user somewhere they can navigate away from.
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Unhandled render error", error, info.componentStack);
  }

  componentDidUpdate(previousProps: Props) {
    if (this.state.failed && previousProps.resetKey !== this.props.resetKey) {
      this.setState({ failed: false });
    }
  }

  render() {
    if (!this.state.failed) {
      return this.props.children;
    }
    if (this.props.fallback !== undefined) {
      return this.props.fallback;
    }
    return <PageErrorFallback />;
  }
}
